import type { SharedData } from "@src/context/ApplicationContext";
import { patchUserLead } from "@src/services/user-lead.service";
import { useCallback, useEffect, useRef } from "react";

interface UseUserLeadBootstrapOptions {
  enabled?: boolean;
  userUuid?: string;
  salesforceLeadId?: string;
  canvasPrefillVersion?: SharedData["canvasPrefillVersion"];
  userRole?: SharedData["userRole"];
  userRecordCreated?: SharedData["userRecordCreated"];
}

/**
 * Posts the Salesforce Lead ID to the backend once per session for PAC
 * (patient navigator) users after the canvas prefill is applied and the
 * user UUID is established.  No-op for non-PAC sessions.
 */
export const useUserLeadBootstrap = ({
  enabled = true,
  userUuid,
  salesforceLeadId,
  canvasPrefillVersion,
  userRole,
  userRecordCreated,
}: UseUserLeadBootstrapOptions) => {
  const hasPostedRef = useRef(false);

  const postUserLead = useCallback(async (currentUserUuid: string, currentLeadId: string) => {
    hasPostedRef.current = true;

    try {
      await patchUserLead(currentUserUuid, currentLeadId);
    } catch {
      hasPostedRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (userRole !== "patient navigator") return;
    if (!canvasPrefillVersion || !userUuid || !salesforceLeadId?.trim()) return;
    // The users row is only created once address-search succeeds; posting
    // before that races ahead of user creation and returns a 404.
    if (!userRecordCreated) return;
    if (hasPostedRef.current) return;

    void postUserLead(userUuid, salesforceLeadId.trim());
  }, [
    enabled,
    userRole,
    canvasPrefillVersion,
    userUuid,
    salesforceLeadId,
    userRecordCreated,
    postUserLead,
  ]);
};
